import { useState, useEffect } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import CreateJobForm from "../components/CreateJobForm"
import JobListingService from "../services/JobListingService"
import OrganizationService from "../services/OrganizationService"
import { useAuth } from "../contexts/AuthProvider"
import ProfileSidebar from "../components/ProfileSideBar"

const CreateJobWrapper = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { jobId } = useParams()
  const { user } = useAuth()

  const [organization, setOrganization] = useState(null)
  const [existingJobDoc, setExistingJobDoc] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("Jobs")

  const isEditMode = location.pathname.includes("/edit")

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return

      try {
        setIsLoading(true)

        const orgData = await OrganizationService.getUserOrganization(user.uid)
        setOrganization(orgData)

        if (isEditMode && jobId && orgData) {
          try {
            const jobListing = await JobListingService.getJobListing(
              orgData.id,
              jobId,
            )
            setExistingJobDoc(jobListing)
          } catch (error) {
            console.error("Error fetching job listing:", error)
            navigate("/jobs")
            return
          }
        }
      } catch (error) {
        console.error("Error fetching organization:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [user?.uid, isEditMode, jobId, navigate])

  useEffect(() => {
    if (isEditMode && !jobId) {
      navigate("/jobs")
    }
  }, [isEditMode, jobId, navigate])

  const handleJobSubmit = async (jobData) => {
    if (!organization) {
      throw new Error("Organization not found")
    }

    try {
      if (isEditMode) {
        await JobListingService.updateJobListing(
          organization.id,
          jobData.id,
          jobData,
        )
        return jobData.id
      } else {
        const newJobId = await JobListingService.createJobListing(
          organization.id,
          jobData,
        )
        return newJobId
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} job:`, error)
      throw error
    }
  }

  const handleCancel = () => {
    navigate("/jobs")
  }

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-white"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <div className="flex">
          <div className="border-r border-zinc-200 h-full bg-white overflow-y-auto min-h-screen">
            <ProfileSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
          <div className="flex-1 ml-60 flex items-center justify-center min-h-screen">
            <div className="flex flex-col items-center gap-4">
              <div
                className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin"
                style={{ borderRadius: 0 }}
              />
              <p
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-400"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Loading...
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <CreateJobForm
      organizationId={organization?.id}
      isEditMode={isEditMode}
      existingJobDoc={existingJobDoc}
      onCancel={handleCancel}
      onSubmit={handleJobSubmit}
    />
  )
}

export default CreateJobWrapper
